// =============================================================================
// features/credit-analyses/hooks/useDocumentChecklist.ts — Checklist de
// documentos consumido por CreditAnalysisDetailPage e ContractDetail (F46-S10).
//
// Consome os dois endpoints derivados de F46-S06 (documents/checklist.service.ts):
//   GET /api/credit-analyses/:id/document-checklist
//   GET /api/contracts/:id/document-checklist
// e a conferência humana de F46-S06:
//   PATCH /api/documents/:id/review
//
// Genérico por `flow` porque `ContractDetail.tsx` (F17) e
// `CreditAnalysisDetailPage.tsx` (F13) precisam do mesmo formato de checklist
// — nada é redigitado aqui, o contrato vem inteiro de
// `@elemento/shared-schemas` (ChecklistResponseSchema/DocumentReviewRequestSchema).
//
// Gate: mesma flag do módulo de documentos (`documents.enabled`) + permissão
// `documents:read` para ler o checklist, `documents:review` para conferir.
// O checklist é SINAL, nunca decisão — nada aqui muda
// `credit_analyses.status`/`contracts.status` (nota do slot).
//
// Invalidação: aprovar/rejeitar invalida a query do checklist (contador não
// pode ficar velho na tela) e também a lista de documentos de
// features/documents (mesma chave usada por DocumentsPanel), para o card do
// documento revisado refletir o novo status sem precisar de F5 manual.
// =============================================================================

import type { ChecklistResponse, Document, DocumentReviewRequest } from '@elemento/shared-schemas';
import {
  ChecklistResponseSchema,
  DocumentReviewRequestSchema,
  DocumentSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth-store';
import { documentsQueryKeys } from '../../documents';

// ---------------------------------------------------------------------------
// Tipos + query keys
// ---------------------------------------------------------------------------

export type ChecklistFlow = 'credit_analysis' | 'contract';

export const documentChecklistQueryKeys = {
  all: ['document-checklist'] as const,
  detail: (flow: ChecklistFlow, contextId: string) =>
    [...documentChecklistQueryKeys.all, flow, contextId] as const,
};

function checklistPath(flow: ChecklistFlow, contextId: string): string {
  const encoded = encodeURIComponent(contextId);
  return flow === 'credit_analysis'
    ? `/api/credit-analyses/${encoded}/document-checklist`
    : `/api/contracts/${encoded}/document-checklist`;
}

async function fetchDocumentChecklist(
  flow: ChecklistFlow,
  contextId: string,
): Promise<ChecklistResponse> {
  const raw = await api.get<unknown>(checklistPath(flow, contextId));
  return ChecklistResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// useDocumentChecklist — leitura
// ---------------------------------------------------------------------------

export interface UseDocumentChecklistResult {
  data: ChecklistResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** true quando a flag `documents.enabled` está ligada E o ator tem `documents:read`. */
  canRead: boolean;
  /** true quando o ator pode aprovar/rejeitar (`documents:review`) — permissão
   * DELIBERADAMENTE distinta de `documents:read` (mesma nota de F46-S08). */
  canReview: boolean;
  /** true enquanto a flag ainda não respondeu — evita "flash" de ações. */
  isGateLoading: boolean;
}

export function useDocumentChecklist(
  flow: ChecklistFlow,
  contextId: string,
): UseDocumentChecklistResult {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading: isGateLoading } = useFeatureFlag('documents.enabled');

  const canRead = flagEnabled && hasPermission('documents:read');
  const canReview = canRead && hasPermission('documents:review');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: documentChecklistQueryKeys.detail(flow, contextId),
    queryFn: () => fetchDocumentChecklist(flow, contextId),
    enabled: canRead && Boolean(contextId),
  });

  return {
    data,
    isLoading,
    isError,
    refetch: () => void refetch(),
    canRead,
    canReview,
    isGateLoading,
  };
}

// ---------------------------------------------------------------------------
// useReviewDocument — conferência humana (aprovar/rejeitar)
// ---------------------------------------------------------------------------

export interface ReviewDocumentArgs {
  documentId: string;
  body: DocumentReviewRequest;
}

/**
 * `flow`/`contextId` só decidem QUAL query de checklist invalidar após a
 * mutação — a chamada de rede em si (`PATCH /api/documents/:id/review`) é a
 * mesma independentemente do fluxo que a disparou.
 */
export function useReviewDocument(flow: ChecklistFlow, contextId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, body }: ReviewDocumentArgs): Promise<Document> => {
      const parsedBody = DocumentReviewRequestSchema.parse(body);
      const raw = await api.patch<unknown>(
        `/api/documents/${encodeURIComponent(documentId)}/review`,
        parsedBody,
      );
      return DocumentSchema.parse(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: documentChecklistQueryKeys.detail(flow, contextId),
      });
      // Mesma chave usada por DocumentsPanel (features/documents/hooks.ts) —
      // o card do documento revisado precisa refletir o novo status sem F5.
      void queryClient.invalidateQueries({ queryKey: documentsQueryKeys.all });
    },
  });
}
